import { ISXClientPrinterHostObject } from './SXClientTypes';
import { SXClientErrorMapper } from './SXClientExceptions';
import { PromiseUtils } from '@utils/PromiseUtils';
import { UUIDUtils } from '@utils/UuidUtils';

/** Timeout for SXClient print operations (30 seconds) */
const SXCLIENT_PRINT_TIMEOUT_MS = 30000;

/**
 * SXClient (Windows WebView2) printing adapter.
 * Routes print jobs to WebView2 host objects for direct printer access.
 *
 * Supports multiple config formats:
 * - Raw string (ZPL): Sent via PrintZebraTextAsync
 * - NetworkPrintingConfig { zplData }: Extracts zplData and prints via Zebra
 * - SXClient JSON format { print: [...] }: Sent via PrintReceiptAsync
 */
export class SXClientPrintingAdapter {
    /**
     * Print using the appropriate method based on config format.
     *
     * @param config Print configuration (string, NetworkPrintingConfig, or SXClient JSON)
     * @returns Promise that resolves when print job completes
     */
    public async print(config: any): Promise<void> {
        const printer = this.getPrinterHostObject();

        console.info('ATJSLib SXClientPrintingAdapter: Config type:', typeof config);

        if (typeof config === 'string') {
            if (config.startsWith('^XA')) {
                console.info('ATJSLib: Printing ZPL payload via Zebra');
            } else {
                console.info('ATJSLib: Printing raw string via Zebra');
            }
            await PromiseUtils.executeWithTimeout(printer.PrintZebraTextAsync(config), {
                durationMillis: SXCLIENT_PRINT_TIMEOUT_MS,
                message: `Print operation timed out after ${SXCLIENT_PRINT_TIMEOUT_MS}ms`,
            });
        } else if (config?.zplData) {
            console.info('ATJSLib: Printing NetworkPrintingConfig via Zebra');
            await PromiseUtils.executeWithTimeout(printer.PrintZebraTextAsync(config.zplData), {
                durationMillis: SXCLIENT_PRINT_TIMEOUT_MS,
                message: `Print operation timed out after ${SXCLIENT_PRINT_TIMEOUT_MS}ms`,
            });
        } else if (config?.print && Array.isArray(config.print)) {
            console.info('ATJSLib: Printing SXClient JSON via receipt printer');
            const activityId = `print-${UUIDUtils.generate()}`;
            await PromiseUtils.executeWithTimeout(printer.PrintReceiptAsync(JSON.stringify(config), activityId), {
                durationMillis: SXCLIENT_PRINT_TIMEOUT_MS,
                message: `Print operation timed out after ${SXCLIENT_PRINT_TIMEOUT_MS}ms`,
            });
        } else {
            console.warn('ATJSLib: Unknown print config format, attempting as raw string');
            await PromiseUtils.executeWithTimeout(printer.PrintZebraTextAsync(JSON.stringify(config)), {
                durationMillis: SXCLIENT_PRINT_TIMEOUT_MS,
                message: `Print operation timed out after ${SXCLIENT_PRINT_TIMEOUT_MS}ms`,
            });
        }

        console.info('ATJSLib: Print job completed successfully');
    }

    /**
     * Print simple text via Zebra.
     */
    public async printText(text: string): Promise<void> {
        const printer = this.getPrinterHostObject();
        await PromiseUtils.executeWithTimeout(printer.PrintZebraTextAsync(text), {
            durationMillis: SXCLIENT_PRINT_TIMEOUT_MS,
            message: `Print operation timed out after ${SXCLIENT_PRINT_TIMEOUT_MS}ms`,
        });
    }

    /**
     * Get the SXClient printer host object from WebView2.
     * @throws Error if printer host object is not available
     */
    private getPrinterHostObject(): ISXClientPrinterHostObject {
        const printer = (window as any).chrome?.webview?.hostObjects?.printer as ISXClientPrinterHostObject;
        if (!printer) {
            throw new Error('SXClient printer host object not available');
        }
        return printer;
    }

    /**
     * Maps a caught error to the appropriate SXClient exception.
     */
    public static mapError(error: any): Error {
        return SXClientErrorMapper.mapPrinterError(error);
    }
}
